import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  Menu as MenuIcon, 
  X, 
  Users, 
  Calendar, 
  Cog, 
  ClipboardCheck, 
  FileText, 
  Zap, 
  Flame, 
  Droplets, 
  Search, 
  Wrench,
  CheckCircle2,
  Shield
} from 'lucide-react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Logo } from './components/Logo';
import { LogoSlider } from './components/LogoSlider';
import { TestimonialSlider } from './components/TestimonialSlider';
import { DemoForm } from './components/DemoForm';
import { WhitepaperCTA } from './components/WhitepaperCTA';
import { MobileMenu } from './components/MobileMenu';
import { LoginModal } from './components/LoginModal';
import { PlatformLayout } from './components/PlatformLayout';
import { CompanyDashboard } from './components/CompanyDashboard';
import { DashboardView } from './components/DashboardView';
import { BookingList } from './components/BookingList';
import { BookingDetails } from './components/BookingDetails';
import { UserManagement } from './components/UserManagement';
import { InvoiceList } from './components/InvoiceList';
import { ClientManagement } from './components/ClientManagement';
import { LocationManagement } from './components/LocationManagement';
import { HeatingComfortPro } from './components/HeatingComfortPro';
import { CompanyManagement } from './components/CompanyManagement';
import { Settings } from './components/Settings';
import { Footer } from './components/Footer';
import { SetPasswordForm } from './components/SetPasswordForm';
import { Language, TranslationProvider, translations, useTranslation } from './translations';
import { LanguageSelector } from './components/LanguageSelector';
import { supabase } from './lib/supabase';
import { Hero } from './components/ui/animated-hero';
import { PremiumTestimonials } from './components/ui/premium-testimonials';

function AppContent() {
  const { t, setLang } = useTranslation();
  const [isDemoFormOpen, setIsDemoFormOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // Set up Supabase auth state listener
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // If there's an error getting the session (like invalid refresh token),
        // clear the session to prevent persistent errors
        console.warn('Session error detected, clearing session:', error.message);
        supabase.auth.signOut();
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(!!session);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Listen for custom event to open demo form
  useEffect(() => {
    const handleOpenDemoForm = () => {
      setIsDemoFormOpen(true);
    };
    
    window.addEventListener('openDemoForm', handleOpenDemoForm);
    
    return () => {
      window.removeEventListener('openDemoForm', handleOpenDemoForm);
    };
  }, []);

  const handleBookingSelect = (id: string) => {
    setSelectedBookingId(id);
  };

  const handleBackToList = () => {
    setSelectedBookingId(null);
  };

  const handleLoginSuccess = () => {
    setIsLoginOpen(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const footerConfig = {
    links: {
      terms: 'https://portal.jaimy.be/terms-conditions',
      privacy: 'https://portal.jaimy.be/legal'
    }
  };

  // For the platform layout
  if (isLoggedIn) {
    return (
      <Router>
        <Routes>
          <Route path="/set-password" element={<SetPasswordForm />} />
          <Route path="*" element={
            <PlatformLayout
              currentSection={currentSection}
              onSectionChange={setCurrentSection}
              onLogout={handleLogout}
            >
              {currentSection === 'dashboard' ? (
                <CompanyDashboard onSectionChange={setCurrentSection} />
              ) : currentSection === 'book-service' ? (
                <DashboardView />
              ) : currentSection === 'bookings' ? (
                selectedBookingId ? (
                  <BookingDetails bookingId={selectedBookingId} onBack={handleBackToList} />
                ) : (
                  <BookingList onBookingSelect={handleBookingSelect} />
                )
              ) : currentSection === 'users' ? (
                <UserManagement />
              ) : currentSection === 'invoicing' ? (
                <InvoiceList />
              ) : currentSection === 'clients' ? (
                <ClientManagement />
              ) : currentSection === 'locations' ? (
                <LocationManagement />
              ) : currentSection === 'heating-comfort' ? (
                <HeatingComfortPro />
              ) : currentSection === 'companies' ? (
                <CompanyManagement />
              ) : currentSection === 'settings' ? (
                <Settings />
              ) : (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">
                      {currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}
                    </h1>
                    <p className="text-gray-600">
                      This section is under development.
                    </p>
                  </div>
                </div>
              )}
            </PlatformLayout>
          } />
        </Routes>
      </Router>
    );
  }

  // For the marketing site
  return (
    <Router>
      <Routes>
        <Route path="/set-password" element={<SetPasswordForm />} />
        <Route path="*" element={
          <div className="min-h-screen bg-white">
            <header className="fixed w-full bg-white/90 backdrop-blur-sm z-50 border-b border-gray-100">
              <nav className="max-w-7xl mx-auto px-1 sm:px-2 lg:px-4 h-20 flex items-center justify-between">
                <Logo />
                <div className="hidden md:flex items-center space-x-8">
                  <a href="#features" className="text-gray-600 hover:text-jaimy-red transition-colors">
                    {t.nav.features}
                  </a>
                  <a href="#solutions" className="text-gray-600 hover:text-jaimy-red transition-colors">
                    {t.nav.solutions}
                  </a>
                  <button
                    onClick={() => setIsLoginOpen(true)}
                    className="text-gray-600 hover:text-jaimy-red transition-colors"
                  >
                    {t.nav.signIn}
                  </button>
                  <LanguageSelector onLanguageChange={setLang} />
                  <button 
                    onClick={() => setIsDemoFormOpen(true)}
                    className="gradient-bg text-white px-6 py-3 rounded-full hover:opacity-90 transition flex items-center gap-2"
                  >
                    {t.cta.button}
                  </button>
                </div>
              </nav>
            </header>

            {/* Hero Section */}
            <Hero />

            {/* Features Section */}
            <section id="features" className="py-24 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.features.title}</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t.features.subtitle}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:bg-gradient-to-br hover:from-jaimy-red hover:to-jaimy-red-dark hover:border-jaimy-red group transition-all duration-200">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-jaimy-red to-jaimy-red-dark flex items-center justify-center mb-6 group-hover:bg-white group-hover:from-white group-hover:to-white transition-colors">
                      <Users className="h-6 w-6 text-white group-hover:text-jaimy-red" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">{t.features.network.title}</h3>
                    <p className="text-gray-600 group-hover:text-white/90 transition-colors">{t.features.network.description}</p>
                  </div>

                  <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:bg-gradient-to-br hover:from-jaimy-red hover:to-jaimy-red-dark hover:border-jaimy-red group transition-all duration-200">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-jaimy-red to-jaimy-red-dark flex items-center justify-center mb-6 group-hover:bg-white group-hover:from-white group-hover:to-white transition-colors">
                      <CheckCircle2 className="h-6 w-6 text-white group-hover:text-jaimy-red" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">{t.features.track.title}</h3>
                    <p className="text-gray-600 group-hover:text-white/90 transition-colors">{t.features.track.description}</p>
                  </div>

                  <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:bg-gradient-to-br hover:from-jaimy-red hover:to-jaimy-red-dark hover:border-jaimy-red group transition-all duration-200">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-jaimy-red to-jaimy-red-dark flex items-center justify-center mb-6 group-hover:bg-white group-hover:from-white group-hover:to-white transition-colors">
                      <Zap className="h-6 w-6 text-white group-hover:text-jaimy-red" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-white transition-colors">{t.features.integration.title}</h3>
                    <p className="text-gray-600 group-hover:text-white/90 transition-colors">{t.features.integration.description}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Logo Slider */}
            <div className="py-16 bg-gradient-to-br from-[#3C5679] to-[#2A3F5F]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-white mb-4">{t.partners.title}</h2>
                  <p className="text-gray-300">{t.partners.subtitle}</p>
                </div>
                <LogoSlider />
              </div>
            </div>

            {/* Solutions Section */}
            <section id="solutions" className="py-24 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-8">{t.solutions.title}</h2>
                    
                    <div className="space-y-3">
                      {t.solutions.items.map((item, index) => (
                        <div 
                          key={index} 
                          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:bg-gradient-to-br hover:from-jaimy-red hover:to-jaimy-red-dark hover:border-jaimy-red group transition-all duration-200 transform hover:-translate-y-1"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-jaimy-red to-jaimy-red-dark flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:from-white group-hover:to-white transition-colors">
                              <ArrowRight className="h-4 w-4 text-white group-hover:text-jaimy-red" />
                            </div>
                            <div>
                              <h3 className="text-base font-medium text-gray-900 group-hover:text-white transition-colors">{item.title}</h3>
                              <p className="text-sm text-gray-600 group-hover:text-white/90 transition-colors mt-1">{item.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="relative lg:-mr-24 xl:-mr-32">
                    <div className="w-full">
                      <img
                        src="https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/Images//Macbook%20MockUp.png"
                        alt="Jaimy Platform"
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 screen-reflection"></div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Testimonial Slider */}
            <PremiumTestimonials />

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-br from-jaimy-red to-jaimy-red-dark text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-4xl font-bold mb-4">{t.cta.title}</h2>
                <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">{t.cta.subtitle}</p>
                <button
                  onClick={() => setIsDemoFormOpen(true)}
                  className="bg-white text-jaimy-red px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
                >
                  {t.cta.button}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </section>

            {/* Footer */}
            <Footer config={footerConfig} />

            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="fixed md:hidden top-6 right-4 w-10 h-10 gradient-bg rounded-lg shadow-lg flex items-center justify-center z-50 hover:opacity-90 transition-opacity"
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5 text-white" strokeWidth={2.5} />
            </button>

            <LoginModal
              isOpen={isLoginOpen}
              onClose={() => setIsLoginOpen(false)}
              onLoginSuccess={handleLoginSuccess}
            />

            <MobileMenu 
              isOpen={isMobileMenuOpen} 
              onClose={() => setIsMobileMenuOpen(false)}
              onDemoClick={() => {
                setIsMobileMenuOpen(false);
                setIsDemoFormOpen(true);
              }}
              onSignInClick={() => {
                setIsMobileMenuOpen(false);
                setIsLoginOpen(true);
              }}
              onLanguageChange={setLang}
              currentLang="NL"
              translations={t}
            />

            <DemoForm isOpen={isDemoFormOpen} onClose={() => setIsDemoFormOpen(false)} />
          </div>
        } />
      </Routes>
    </Router>
  );
}

const App = () => {
  const [lang, setLang] = useState<Language>('NL');

  return (
    <TranslationProvider value={{ lang, t: translations[lang], setLang }}>
      <AppContent />
    </TranslationProvider>
  );
};

export default App;